import {
    useCheckTeams,
    useCreateTeam,
    useGetTeamById,
    useInviteUser,
    useJoinTeam,
    useLeaveTeam,
} from '@/features/auth/api';
import NoTeamPage from '@/features/auth/no-team-page';
import TeamDashboard from '@/features/auth/team-dashboard';
import { User } from '@/features/auth/types';
import { useQueryClient } from '@tanstack/react-query';
import {
    createFileRoute,
    useNavigate,
    useSearch,
} from '@tanstack/react-router';
import { z } from 'zod';

import { useCurrentUser } from '@/hooks/useApiKey';
import { useHasTeam } from '@/hooks/useHasTeam';

import { LoadingScreen } from '@/components/loading-screen';

const teamsSearchSchema = z.object({
    isNewUser: z.boolean().optional(),
});

export const Route = createFileRoute('/teams')({
    component: TeamPage,
    validateSearch: teamsSearchSchema,
});

function TeamPage() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { isNewUser } = useSearch({ from: '/teams' });
    const [currentUser, _, userLoading, refreshUser] = useCurrentUser();

    const createTeam = useCreateTeam();
    const joinTeam = useJoinTeam();
    const inviteUser = useInviteUser();
    const leaveTeam = useLeaveTeam();
    const hasTeam = useHasTeam();

    const { data: currentTeam } = useGetTeamById(currentUser?.teamId || null);
    const { data: checkTeamsData } = useCheckTeams();

    const refreshTeamAndUser = () => {
        refreshUser();

        if (currentUser?.teamId) {
            queryClient.invalidateQueries({
                queryKey: ['teams', 'team', currentUser.teamId],
            });
        }
    };

    const handleTeamCreated = (teamName: string) => {
        createTeam.mutate(teamName, {
            onSuccess: () => {
                refreshTeamAndUser();
                navigate({ to: '/teams', search: { isNewUser: true } });
            },
        });
    };

    const handleTeamJoined = (teamCode: string) => {
        joinTeam.mutate(teamCode, {
            onSuccess: () => {
                refreshTeamAndUser();
                navigate({ to: '/teams', search: { isNewUser: true } });
            },
        });
    };

    const handleLeaveTeam = () => {
        leaveTeam.mutate(undefined, {
            onSuccess: () => {
                refreshTeamAndUser();
            },
        });
    };

    const handleInviteMember = (username: string) => {
        inviteUser.mutate(
            { username, teamCode: currentTeam?.teamCode || '' },
            {
                onSuccess: () => {
                    refreshTeamAndUser();
                },
            },
        );
    };

    const navigateToHome = () => {
        navigate({ to: '/' });
    };

    return (
        <div className="flex flex-1 min-h-full" data-testid="Teams">
            {typeof currentUser === 'undefined' && !userLoading ? (
                <LoadingScreen />
            ) : hasTeam && currentTeam ? (
                <TeamDashboard
                    isNewUser={isNewUser || false}
                    navigateToHome={navigateToHome}
                    team={currentTeam}
                    currentUser={currentUser as User}
                    onLeaveTeam={handleLeaveTeam}
                    onInviteMember={handleInviteMember}
                />
            ) : (
                <NoTeamPage
                    hasTeams={checkTeamsData?.hasTeams || false}
                    onTeamCreated={handleTeamCreated}
                    onTeamJoined={handleTeamJoined}
                />
            )}
        </div>
    );
}
